import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Alert,
  Paper,
  Typography,
  Breadcrumbs,
  Link,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { NavigateNext as NavigateNextIcon } from '@mui/icons-material';
import ClientForm from '@/components/ClientForm';
import { Client, ClientFormData } from '@/types';
import { ClientService } from '@/services/clientService';
import { useAuthStore } from '@/store/authStore';

const ClientEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!id) {
      setError('Client ID is required');
      setLoading(false);
      return;
    }

    const loadClient = async () => {
      try {
        setLoading(true);
        setError(null);
        const clientData = await ClientService.getClient(id);
        
        if (!clientData) {
          setError('Client not found');
          return;
        }

        // Check permission (client-side check, also enforced on server)
        if (user?.role !== 'admin' && user?.id !== clientData.agentId) {
          setError('You do not have permission to edit this client');
          return;
        }

        setClient(clientData);
      } catch (err: any) {
        setError(err.message || 'Failed to load client');
      } finally {
        setLoading(false);
      }
    };

    loadClient();
  }, [id, user]);

  const handleUpdateClient = async (data: ClientFormData) => {
    if (!client || !id) return;

    setSaving(true);
    try {
      await ClientService.updateClient(client.id, data);
      navigate(`/clients/${client.id}`);
    } catch (err: any) {
      console.error('Update error:', err);
      setError(err.message || 'Failed to update client');
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to leave? Unsaved changes will be lost.')) {
      navigate(`/clients/${id}`);
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!client) return;
    
    setDeleting(true);
    try {
      await ClientService.deleteClient(client.id, false); // Soft delete
      navigate('/clients');
    } catch (err: any) {
      setError(err.message || 'Failed to delete client');
      setDeleteDialogOpen(false);
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !client) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Client not found'}
        </Alert>
        <Button variant="contained" onClick={() => navigate('/clients')}>
          Back to Clients
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
          <Link color="inherit" href="/dashboard" onClick={(e) => { e.preventDefault(); navigate('/dashboard'); }}>
            Dashboard
          </Link>
          <Link color="inherit" href="/clients" onClick={(e) => { e.preventDefault(); navigate('/clients'); }}>
            Clients
          </Link>
          <Link color="inherit" href={`/clients/${id}`} onClick={(e) => { e.preventDefault(); navigate(`/clients/${id}`); }}>
            {client.profile.firstName} {client.profile.lastName}
          </Link>
          <Typography color="text.primary">Edit</Typography>
        </Breadcrumbs>
        
        <Button 
          variant="outlined" 
          color="error" 
          onClick={handleDeleteClick}
        >
          Delete Client
        </Button>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Edit Client: {client.profile.firstName} {client.profile.lastName}
        </Typography>
        
        <ClientForm
          client={client}
          onSubmit={handleUpdateClient}
          onCancel={handleCancel}
          loading={saving}
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {"Delete Client?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete "{client.profile.firstName} {client.profile.lastName}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientEditPage;

